<template>
  <Transition
    :name="parentWrapper === 'app' ? animationClass : headerAnimationClass"
  >
    <toast-view
      v-if="isOpen"
      :class="[positionStyle, parentWrapper === 'app' ? 'fixed' : '']"
      :style="[widthStyle]"
      :notifications="getToastNotifications"
      :urgent-notifications="parentWrapper === 'app' ? urgentNotifications : []"
      :automations="parsedAutomations"
      :ticket-context="parsedTicketContext"
      :config="getConfig()"
      :context="{ users }"
      :parent-wrapper="parentWrapper"
      @go-to-entity="handleGoToEntity"
      @close-toast-item="handleCloseToastItem"
      @go-to-notifications="handleGoToNotifications"
    />
  </Transition>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  useAdminStore,
  useToastStore,
  useNotificationStore,
} from '@getmayday/vue-modules';
import useEntities from '../../../composables/useEntities';
import useLogs from '../../../composables/useLogs';
import { MIN_WIDTH } from '../../../stores/config';

const props = defineProps({
  parentWrapper: {
    type: String as PropType<'app' | 'header'>,
    required: true,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();

const toastStore = useToastStore();
const { toggleIsGroupOpen } = toastStore;
const notificationStore = useNotificationStore();
const adminStore = useAdminStore();
const { readNotifications, updateFilters } = notificationStore;
const { getToastNotifications, urgentNotifications, users } =
  storeToRefs(notificationStore);
const { recommendations, ticketContext, automationsDelay, askIsLoading } =
  storeToRefs(adminStore);
const { updateRecommendations, updateTicketContext } = adminStore;

const configStore = useConfigStore();
const { position, positionIsLeft } = storeToRefs(configStore);

const { goToEntity, getConfig } = useEntities();

const { appToastReceive } = useLogs();
const automationTimeout = reactive<{ [key: string]: any }>({});
const handleCloseToastItem = ({
  id,
  type,
}: {
  id: string;
  type: string;
  isUrgent: boolean;
}) => {
  if (type === 'AUTOMATION') {
    updateRecommendations(
      recommendations.value.filter((r) => r.treeNodeId !== id),
    );
  } else if (type === 'TICKET_CONTEXT') {
    updateTicketContext(null);
  } else if (type === 'NOTIFICATION') {
    readNotifications([id], false);
  }
};

const handleGoToEntity = ({
  id,
  type,
  toastType,
  notificationId,
}: {
  id: string;
  type: string;
  toastType: string;
  notificationId: string;
}) => {
  goToEntity({ id, type, deepLoading: true, fromToast: true });
  if (notificationId && toastType === 'NOTIFICATION') {
    readNotifications([notificationId]);
  } else if (toastType === 'AUTOMATION') {
    updateRecommendations(
      recommendations.value.filter((r) => r.treeNodeId !== id),
    );
  }
};

const handleGoToNotifications = () => {
  updateFilters({ field: 'unreadOnly', value: true });
  router.push('/notifications');
};

const POSITION_STYLES: {
  [key in AppPosition]: { position: string; animation: string };
} = {
  'top-left': {
    position: 'left-5 bottom-5 top-20',
    animation: 'scroll-to-bottom',
  },
  'top-right': {
    position: 'right-5 bottom-5 top-20',
    animation: 'scroll-to-bottom',
  },
  'bottom-left': {
    position: 'left-5 top-5 bottom-20',
    animation: 'scroll-to-top',
  },
  'bottom-right': {
    position: 'right-5 top-5 bottom-20',
    animation: 'scroll-to-top',
  },
};

const animationClass = computed(() => {
  return POSITION_STYLES[position.value].animation;
});

const headerAnimationClass = computed(() =>
  positionIsLeft.value ? 'scroll-to-right' : 'scroll-to-left',
);

const positionStyle = computed(() => {
  return POSITION_STYLES[position.value].position;
});

const widthStyle = computed(() =>
  props.parentWrapper === 'header' ? 'width: 100%' : `width: ${MIN_WIDTH}px`,
);

watch(
  () => props.isOpen,
  () => {
    setTimeout(() => {
      toggleIsGroupOpen(true, 'URGENT_NOTIFICATION');
      toggleIsGroupOpen(true, 'NOTIFICATION');
      toggleIsGroupOpen(true, 'AUTOMATION');
    }, 100);
  },
);

watch(
  () => recommendations.value,
  (newValue) => {
    if (automationsDelay.value && newValue.length && !askIsLoading.value) {
      clearTimeout(automationTimeout.value);
      automationTimeout.value = setTimeout(() => {
        updateRecommendations([]);
        toastStore.denyAutomations(recommendations.value);
      }, automationsDelay.value);
    }
  },
  { immediate: true },
);

const parsedAutomations = computed(() => {
  return recommendations.value.map((recommendation: any) => ({
    id: recommendation.treeNodeId,
    title: recommendation.alias,
    entity: {
      id: recommendation.treeNodeId,
      type: recommendation.entityType || 'Step',
    },
    recommendation,
  }));
});

const parsedTicketContext = computed(() => {
  return ticketContext.value
    ? {
        context: ticketContext.value.user_request || '',
        sentiment: ticketContext.value.sentiment,
      }
    : undefined;
});

watch(parsedTicketContext, (newVal, oldVal) => {
  if (
    newVal &&
    newVal.context !== oldVal?.context &&
    newVal.sentiment !== oldVal?.sentiment
  ) {
    appToastReceive(ticketContext.value?.source);
  }
});
</script>

<style scoped></style>
